import { QueryStatus } from '@reduxjs/toolkit/query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useSfideInArrivo } from '@/hooks/useSfideInArrivo';
import { SFIDE_IN_ARRIVO } from '@/lib/limulo/challenge';
import { useSfideGestite } from '@/lib/limulo/sfideGestite';
import { SFIDE_INVIATE } from '@/lib/limulo/sfideTab';
import { useGetLimuloChallengesQuery } from '@/services/limuloApi';
import { useAppSelector } from '@/store/hooks';

/**
 * **La sfida messa in evidenza dal deep link** (T2-5, lotto B3b-1): `/friends?tab=duels&sfida=<challengeId>`.
 *
 * ═══ L'ID E' UN INPUT NON FIDATO ═══
 * Arriva da un link che chiunque puo' scrivere. Qui si confronta SOLO con le due liste che l'utente ha GIA' (le in arrivo
 * dalla cache della barra, le inviate dalla lettura del tab): mai una GET per id (`/v1/challenges/<id>`), che direbbe al
 * server «dammi questa sfida» su un valore scelto da altri. Dal link non deve trapelare niente su una sfida non tua: se
 * l'id non e' in NESSUNA delle due liste, la frase e' UNA, neutra, per tutti i casi (scaduta, con risposta, di un altro,
 * inventata) — `social.sfida_link_non_disponibile`, mai uno stile d'errore.
 *
 * ═══ QUATTRO STATI, e la frase e' solo l'ultimo ═══
 *  · `nessuna`  — nessun parametro: questo hook non legge niente (le due query sono in `skip`), costo zero;
 *  · `cerca`    — non si sa ancora: una lista deve ARRIVARE, o e' arrivata PRIMA del parametro, o e' in ERRORE (con o senza dati
 *                 vecchi) e la sfida non e' nell'altra. Nessuna frase, nessun lampo, e il parametro RESTA. L'ERRORE non ha uno
 *                 stato suo: e' il lavoro del tab (`SfideDiPiattaformaTab`, i riquadri `…-errore`), non di questo hook, e nessuno
 *                 fuori di qui avrebbe potuto distinguerlo da `cerca`;
 *  · `trovata`  — in una delle due liste, ANCHE nella cache vecchia (una sfida nella lista si segna subito, e una lista che
 *                 ha dati vecchi e un'ultima rilettura fallita la contiene lo stesso): `aria-current`, si porta in vista;
 *  · `assente`  — entrambe le liste sono state lette DOPO l'arrivo del parametro, e lette bene, e la sfida non c'e': la frase.
 *
 * ═══ «LETTA DOPO» e «IN ERRORE», le due definizioni che decidono la frase ═══
 *  · «letta dopo» (per lista) = la promessa della rilettura lanciata all'arrivo e RIUSCITA, OPPURE una risposta riuscita di una
 *    richiesta PARTITA dopo l'arrivo (`startedTimeStamp >= t_arrivo`, `t_arrivo` preso PRIMA di chiamare `refetch`). Conta l'INIZIO
 *    della richiesta, mai la fine: a fine richiesta una cache calda scritta un istante prima del link e una lettura vera sono
 *    indistinguibili (l'intermittenza del cantiere 7 stava su `fulfilledTimeStamp`). Cosi' una rilettura d'arrivo esaurita in
 *    errore (500 e ritentativo 500) non chiude la frase per tutta la vita del tab: la prima lettura buona che parte dopo la apre.
 *  · «in errore» = l'ULTIMA lettura CONCLUSA e' fallita, e lo resta fino a un successo (`error !== undefined`: RTK lo tiene
 *    mentre il giro successivo e' in volo e lo toglie solo a una risposta riuscita). `isError` no: torna falso mentre il giro
 *    dopo e' `pending`, e la frase usciva proprio li', accanto al riquadro d'errore.
 *
 * ═══ LA CACHE VECCHIA SI CORREGGE (tma-orcr, 29/9 18:21Z) ═══
 * Il link arriva da una notifica, quindi quasi sempre per una sfida APPENA CREATA: con la cache della barra vecchia (fino a
 * 60 s) «non e' piu' disponibile» sarebbe la frase falsa peggiore. All'ARRIVO del parametro — al primo atterraggio E a ogni
 * link successivo a tab aperto — si rileggono le due liste, in arrivo e inviate: `refetch` della STESSA query con gli STESSI
 * argomenti della barra e del tab (mai una GET per id). E la frase si dice SOLO su liste lette dopo quell'istante
 * (l'esito riuscito della `refetch` lanciata all'arrivo, o una risposta riuscita di una richiesta partita dopo: v. «LETTA DOPO»),
 * mai su cio' che c'era gia' in cache.
 * ⚠️ IL COSTO, misurato in `sfida-deep-link-costo`: una GET in piu' per lista, solo quando c'e' `sfida=` E la lista non e' gia'
 * in volo (a cache fredda la lettura iniziale del tab e' gia' «dopo l'arrivo»: RTK non lancia due richieste per la stessa
 * chiave). Senza parametro, nessuna. ⚠️ **Il ritmo si RIANCORA**: RTK riparte il timer di polling a ogni risposta, quindi dopo la
 * rilettura la lista riprende a 60 s DALLA rilettura (le inviate: `[0, T, T+60, T+120]`); non si rompe, si sposta.
 *
 * ═══ COSA SUCCEDE AL PARAMETRO ═══
 * L'id si tiene in uno STATO locale, e a esito raggiunto (`trovata` o `assente`) il parametro si TOGLIE dall'URL con
 * `replace`: l'evidenza e' un atterraggio, non una vista da ricostruire. Cosi' (1) «indietro» non torna sull'evidenza —
 * la voce di cronologia e' stata sostituita, non aggiunta —, (2) una ricarica non rimette in evidenza una sfida ormai
 * vecchia, (3) non c'e' nessun giro a vuoto: tolto il parametro, l'effetto non rigira. Il resto della query (`tab=duels`)
 * non si tocca. ⚠️ In `cerca` (compresa una lista in errore) il parametro RESTA: toglierlo prima di sapere perderebbe l'unica cosa che il
 * link portava. Se arriva un id NUOVO mentre il tab e' gia' aperto (un secondo link), l'evidenza riparte da quello.
 *
 * ═══ IL LATCH DELLA TROVATA tace la frase SOLO in due casi ═══
 * Una sfida che e' uscita dalla lista non fa comparire «non disponibile» se (a) l'utente ha AGITO su quella sfida (e' in `gestite`,
 * dopo che la riga era in vista: l'ho appena accettata, la frase starebbe accanto all'esito di quello che ho fatto) oppure (b) e'
 * stata trovata in una lista letta DOPO l'arrivo. NON basta averla vista nella cache vecchia: se il server dopo la rilettura non
 * l'ha piu' e nessuno ha agito, la riga che e' lampeggiata era una sfida gia' chiusa e la frase e' dovuta.
 *
 * ═══ RAGGIUNGIBILITA' [D] ═══ Un arrivo a sessione non pronta (`!authed`) si PERDE: l'effetto dell'arrivo dipende solo da `[arrivo]`
 * e a sessione pronta non riparte. Non l'ho trovato raggiungibile (il tab si monta sotto `isAuthenticated = bridge || sdk`, e in
 * Telegram la strada e' il bridge): la guardia `!authed` c'e' perche' RTK lancia «Cannot refetch a query that has not been started
 * yet» su una query in `skip`, e ha il suo test. Mettere `authed` fra le dipendenze costerebbe una rilettura a ogni passaggio di
 * sessione.
 */
export type StatoEvidenza = 'nessuna' | 'cerca' | 'trovata' | 'assente';

export interface EvidenzaSfida {
  /** L'id da segnare fra le righe (o `null`): le righe lo confrontano col proprio `challengeId`, mai altro. */
  id: string | null;
  stato: StatoEvidenza;
  /** La frase neutra va mostrata: entrambe le liste sono state lette dopo l'arrivo, bene, e l'id non e' in nessuna. */
  assente: boolean;
  /** Una riga con quell'id e' A SCHERMO adesso (in una delle due liste che il tab rende): solo allora la riga e' davvero in evidenza. */
  rigaVisibile: boolean;
}

/** L'arrivo di un parametro. Un OGGETTO nuovo a ogni arrivo (anche allo stesso id): la sua identita' e' cio' che lo distingue. */
interface Arrivo {
  id: string;
}

export function useEvidenzaSfida(): EvidenzaSfida {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('sfida');
  const [arrivo, setArrivo] = useState<Arrivo | null>(() => (param === null ? null : { id: param }));
  const id = arrivo?.id ?? null;
  // Ogni comparsa di un parametro e' un ARRIVO: un id nuovo mentre il tab e' aperto (un secondo link) riparte da capo. Il
  // parametro che CADE (lo togliamo noi, a esito raggiunto) non e' un arrivo.
  const ultimoParam = useRef<string | null>(param);
  useEffect(() => {
    if (param === ultimoParam.current) return;
    ultimoParam.current = param;
    if (param !== null) setArrivo({ id: param });
  }, [param]);

  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const attiva = authed && id !== null;
  const inArrivo = useSfideInArrivo();
  const gestite = useSfideGestite();
  // Le due query sono le STESSE della barra e del tab (una voce di cache ciascuna, stessi argomenti): qui servono lo stato, il
  // timestamp d'inizio dell'ultima richiesta, l'errore e `refetch`. `skip` senza parametro: fuori da un deep link non sottoscrivono.
  const inQ = useGetLimuloChallengesQuery(SFIDE_IN_ARRIVO, { skip: !attiva });
  const outQ = useGetLimuloChallengesQuery(SFIDE_INVIATE, { skip: !attiva });
  const { refetch: rileggiIn } = inQ;
  const { refetch: rileggiOut } = outQ;

  // «LETTA DOPO» (per lista), vedi il docblock: la promessa della rilettura d'arrivo, riuscita, OPPURE una risposta riuscita di una
  // richiesta partita dopo `t` (l'istante d'arrivo, preso PRIMA di chiamare `refetch`). `t` sta nello stato con i due flag, cosi' il
  // confronto in render usa lo stesso valore dell'effetto che ha lanciato la lettura.
  const [lette, setLette] = useState<{
    arrivo: Arrivo;
    t: number;
    in: boolean;
    out: boolean;
  } | null>(null);
  useEffect(() => {
    // Un arrivo a sessione non pronta si perde (docblock, RAGGIUNGIBILITA' [D]): senza questa guardia `refetch` su una query in
    // `skip` LANCIA e l'albero cade.
    if (arrivo === null || !authed) return;
    let vivo = true;
    const t = Date.now();
    setLette({ arrivo, t, in: false, out: false });
    // ⚠️ `isSuccess` NON si toglie: se all'arrivo c'e' una lettura in volo che RTK non ha registrato fra le `runningQueries` (una
    // mutazione che invalida `LimuloChallenge` e' finita durante una lettura: RTK rimanda l'invalidazione e la lettura nuova parte
    // senza registrarsi), la `refetch` e' scartata dalla condizione e la sua promessa si risolve SUBITO con `pending`. Senza questo
    // controllo quella risposta varrebbe «letta dopo» e la frase uscirebbe in volo su una cache vecchia (test X1). Le due guardie che
    // seguono, `vivo` e `prev?.arrivo === arrivo`, sono in serie e ridondanti (nessun input le separa [D]): restano entrambe.
    const segna = (quale: 'in' | 'out') => (r: { isSuccess?: boolean }) => {
      if (!vivo || r.isSuccess !== true) return;
      setLette((prev) => (prev?.arrivo === arrivo ? { ...prev, [quale]: true } : prev));
    };
    void Promise.resolve(rileggiIn()).then(segna('in'));
    void Promise.resolve(rileggiOut()).then(segna('out'));
    return () => {
      vivo = false;
    };
    // Solo all'arrivo: `authed`/`refetch` non devono rilanciare la lettura (la regola non se ne lamenta: niente direttiva).
  }, [arrivo]);
  // La seconda via: una risposta riuscita di una richiesta partita DOPO l'arrivo. ⚠️ Il confronto `>= t` NON e' ridondante: con una
  // lettura non registrata (v. sopra) la sua risposta arriva `fulfilled` ma e' PARTITA prima dell'arrivo, e non vale (test X1).
  // Si LATCHA nello stato (una rilettura successiva rimette la query in `pending` e il confronto in render tornerebbe falso: la
  // frase lampeggerebbe). `status` e non `isSuccess`: il flag dell'hook e' vero anche mentre un giro nuovo e' in volo con dati vecchi.
  const inFulfilled = inQ.status === QueryStatus.fulfilled ? (inQ.startedTimeStamp ?? -1) : -1;
  const outFulfilled = outQ.status === QueryStatus.fulfilled ? (outQ.startedTimeStamp ?? -1) : -1;
  useEffect(() => {
    if (lette === null || lette.arrivo !== arrivo) return;
    const inOk = !lette.in && inFulfilled >= lette.t;
    const outOk = !lette.out && outFulfilled >= lette.t;
    if (!inOk && !outOk) return;
    setLette((prev) => (prev?.arrivo === arrivo ? { ...prev, in: prev.in || inOk, out: prev.out || outOk } : prev));
  }, [lette, arrivo, inFulfilled, outFulfilled]);
  const lettaDopoIn = lette !== null && lette.arrivo === arrivo && lette.in;
  const lettaDopoOut = lette !== null && lette.arrivo === arrivo && lette.out;
  const lettaDopo = lettaDopoIn && lettaDopoOut;

  // In quale lista sta l'id: `inArrivo.vive` (la lista che il tab RENDE: senza le gestite e senza le non-`pending`), mai il dato
  // crudo della query, che contiene righe che nessuno vede.
  const inInArrivo = id !== null && inArrivo.vive.some((c) => c.challengeId === id);
  const inInviate = id !== null && (outQ.data?.items ?? []).some((c) => c.challengeId === id);
  const inLista = inInArrivo || inInviate;
  // IL LATCH (docblock): tace la frase solo se l'utente ha AGITO su quella sfida dopo averla vista, o se e' stata trovata in una
  // lista letta DOPO l'arrivo. Sta per ARRIVO, non per id: lo stesso link due volte e' due arrivi.
  const [visto, setVisto] = useState<Arrivo | null>(null);
  const [vistoDopo, setVistoDopo] = useState<Arrivo | null>(null);
  useEffect(() => {
    if (arrivo === null) return;
    if (inLista && visto !== arrivo) setVisto(arrivo);
    if (((inInArrivo && lettaDopoIn) || (inInviate && lettaDopoOut)) && vistoDopo !== arrivo) setVistoDopo(arrivo);
  }, [inLista, inInArrivo, inInviate, lettaDopoIn, lettaDopoOut, arrivo, visto, vistoDopo]);
  const agito = id !== null && gestite.has(id);
  const tace = arrivo !== null && (vistoDopo === arrivo || (visto === arrivo && agito));
  const trovata = inLista || tace;

  // «IN ERRORE» = l'ultima lettura CONCLUSA e' fallita, e lo resta fino a un successo (docblock). UNA regola per le due liste, con
  // o senza dati vecchi. «Non e' piu' disponibile» e' un'affermazione, e si fa solo su liste lette bene.
  const inErrore = inQ.error !== undefined;
  const outErrore = outQ.error !== undefined;
  const inErroreOut = inErrore || outErrore;
  const assenteOra = !inErroreOut && lettaDopo;
  // L'esito «assente» si TIENE per quell'arrivo: una rilettura in errore DOPO che la frase e' comparsa non la fa sparire
  // (una frase che compare e poi ritira sarebbe un secondo messaggio, e falso quanto il primo).
  const [assenteArrivo, setAssenteArrivo] = useState<Arrivo | null>(null);
  useEffect(() => {
    if (assenteOra && arrivo !== null && assenteArrivo !== arrivo) setAssenteArrivo(arrivo);
  }, [assenteOra, arrivo, assenteArrivo]);
  const assenteTenuta = arrivo !== null && assenteArrivo === arrivo;
  const stato: StatoEvidenza =
    id === null ? 'nessuna' : trovata ? 'trovata' : assenteOra || assenteTenuta ? 'assente' : 'cerca';

  // Esito raggiunto ⇒ il parametro si toglie (replace), una volta sola per id.
  useEffect(() => {
    if (param === null || param !== id) return;
    if (stato !== 'trovata' && stato !== 'assente') return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('sfida');
        return next;
      },
      { replace: true },
    );
  }, [stato, param, id, setSearchParams]);

  return { id, stato, assente: stato === 'assente', rigaVisibile: inLista };
}
